import type { SupervisorFinding } from '@avisoma/shared';
import axios from 'axios';
import { formatValue } from './formatValue';

export type FindingActionKind = 'apply' | 'revert' | 'dismiss' | 'restore';

const DONE: Record<FindingActionKind, string> = {
  apply: 'Correction applied',
  revert: 'Correction reverted',
  dismiss: 'Finding dismissed',
  restore: 'Restored; it returns to the list after the next finalize',
};

/**
 * Toast for a finished finding action. Apply and revert answer 200 with `stale` when the write
 * check failed (re-extraction, row gone, value changed) and nothing was written; that is an
 * error, not a success. `stale` is sticky on the server (a later run never raises the same
 * row+field again), so the toast does not promise a re-raise. `observedValue` is null when the
 * row is missing or ambiguous or the entity is gone.
 */
const staleMessage = (kind: FindingActionKind, finding: SupervisorFinding): string => {
  const prefix = kind === 'revert' ? 'Not reverted' : 'Not applied';
  return finding.observedValue === null
    ? `${prefix}: the row no longer matches the finding. Nothing was written.`
    : `${prefix}: nothing was written. The entity now holds ${formatValue(finding.observedValue)}.`;
};

export const findingMutationToast = (
  kind: FindingActionKind,
  finding: SupervisorFinding,
): { type: 'success' | 'error'; message: string } =>
  finding.status === 'stale'
    ? { type: 'error', message: staleMessage(kind, finding) }
    : { type: 'success', message: DONE[kind] };

export const INVALID_TRANSITION_MESSAGE = 'Finding changed, list refreshed';

/** Toast for a failed finding action: a 409 `invalid_transition` is a refresh, anything else an error. */
export const findingMutationErrorToast = (error: unknown): { type: 'info' | 'error'; message: string } => {
  const status = axios.isAxiosError(error) ? error.response?.status : undefined;
  const serverMessage = axios.isAxiosError(error)
    ? (error.response?.data as { error?: { message?: string } } | undefined)?.error?.message
    : undefined;
  if (status === 409 && serverMessage === 'invalid_transition') return { type: 'info', message: INVALID_TRANSITION_MESSAGE };
  const detail = error instanceof Error ? error.message : 'Unknown error';
  return { type: 'error', message: serverMessage ? `${detail}: ${serverMessage}` : detail };
};
